import { useQuery } from "react-query";
import { Activity, Bot, CalendarClock, Crown, ExternalLink, KeyRound, ListChecks, LogIn, ScrollText, ShieldAlert, UserRound, Zap } from "lucide-react";
import { Button } from "../../../ui/button";
import { saasApi, saasErrorMessage } from "../shared/saasApi";
import { formatDate, formatNumber, formatRelative, humanize } from "../shared/saasFormat";
import { ActivityTimelineChart } from "../shared/SaasCharts";
import { BreakdownBars } from "../shared/SaasUi";
import DetailModal, { DetailChip, DetailEmpty, DetailField, DetailOverview, DetailPanel, DetailSection } from "./DetailModal";
import EventList from "./EventList";
import FindingCard from "./FindingCard";
import GrantsTable from "./GrantsTable";
import { WorkspaceContext } from "./workspaceContext";

export default function IdentityDetailModal({ context, connectionId, login, onClose }: {
    context: WorkspaceContext;
    connectionId: number;
    login: string;
    onClose: () => void;
}) {
    const query = useQuery(["saas-identity-detail", context.provider, context.tenantName, connectionId, login, context.range], () =>
        saasApi.identityDetail(context.provider, context.tenantName, connectionId, login, context.range));
    const detail = query.data;
    const identity = detail?.identity;

    return (
        <DetailModal
            onClose={onClose}
            title={login}
            subtitle={identity ? [identity.displayName, identity.email].filter(Boolean).join(" · ") || "Identity" : "Identity not in current inventory"}
            icon={identity?.avatarUrl ? <img src={identity.avatarUrl} alt="" className="w-full h-full object-cover" />
                : identity?.identityType === "BOT" ? <Bot /> : <UserRound />}
            chips={identity && (
                <>
                    {identity.highestPermission && identity.highestPermission !== "NONE" && <DetailChip tone="type">{humanize(identity.highestPermission)}</DetailChip>}
                    {identity.role && <DetailChip>{identity.role}</DetailChip>}
                    {identity.identityType === "BOT" && <DetailChip>Bot</DetailChip>}
                    {identity.dormant && <DetailChip tone="severity">Dormant</DetailChip>}
                    {identity.outsideCollaborator && <DetailChip tone="severity">Outside collaborator</DetailChip>}
                    {identity.twoFactorEnabled === false && <DetailChip tone="severity">2FA disabled</DetailChip>}
                    {identity.siteAdmin && <DetailChip tone="severity">Site admin</DetailChip>}
                </>
            )}
            actions={identity?.webUrl && (
                <Button variant="outline" className="saas-small-button" asChild>
                    <a href={identity.webUrl} target="_blank" rel="noreferrer">
                        <ExternalLink className="w-4 h-4" />
                        Profile
                    </a>
                </Button>
            )}
        >
            {query.isLoading ? (
                <div className="space-y-4">
                    <div className="h-[66px] rounded-xl border border-border animate-pulse" />
                    <div className="h-64 rounded-xl border border-border animate-pulse" />
                </div>
            ) : query.isError || !detail ? (
                <DetailEmpty>{saasErrorMessage(query.error, "Unable to load this identity")}</DetailEmpty>
            ) : (
                <>
                    <DetailOverview>
                        <DetailField icon={<Activity />} label="Events" value={`${formatNumber(detail.activity.eventCount)} · ${formatNumber(detail.activity.highRiskCount)} high risk`} />
                        <DetailField icon={<KeyRound />} label="Access grants"
                                     value={`${formatNumber(detail.grants.length)} · ${formatNumber(detail.grants.filter((grant) => ["MAINTAIN", "ADMIN", "OWNER"].includes(grant.permission)).length)} privileged`} />
                        <DetailField icon={<CalendarClock />} label="Last activity" value={formatRelative(identity?.lastActivityAt, "No activity recorded")} />
                        <DetailField icon={<ShieldAlert />} label="Open findings" value={formatNumber(detail.findings.length)} />
                        <DetailField icon={<Crown />} label="Highest permission" value={humanize(identity?.highestPermission) || "—"} />
                        <DetailField icon={<LogIn />} label="Last sign-in" value={formatRelative(identity?.lastSignInAt, "—")} />
                        <DetailField icon={<UserRound />} label="Account created" value={formatDate(identity?.platformCreatedAt)} />
                        <DetailField icon={<ShieldAlert />} label="Two-factor"
                                     value={identity?.twoFactorEnabled === false ? "Disabled" : identity?.twoFactorEnabled ? "Enabled" : "Not visible"} />
                    </DetailOverview>

                    {detail.findings.length > 0 && (
                        <DetailSection icon={<ListChecks />} title="Open findings">
                            <div className="space-y-2">
                                {detail.findings.map((finding) => (
                                    <FindingCard key={finding.id} finding={finding} context={context} compact onChanged={() => query.refetch()} />
                                ))}
                            </div>
                        </DetailSection>
                    )}

                    <DetailSection icon={<Activity />} title="Activity">
                        <DetailPanel>
                            <ActivityTimelineChart activity={detail.activity} height={220} />
                        </DetailPanel>
                    </DetailSection>

                    <DetailSection icon={<KeyRound />} title="Access">
                        <GrantsTable grants={detail.grants} mode="identity" />
                    </DetailSection>

                    <div className="saas-split">
                        <DetailSection icon={<Zap />} title="Actions used">
                            <DetailPanel className="max-h-80 overflow-y-auto">
                                <BreakdownBars items={detail.activity.actions} onSelect={(item) => context.openEvents({ actor: login, action: item.key })} />
                            </DetailPanel>
                        </DetailSection>
                        <DetailSection icon={<ScrollText />} title="Resources touched">
                            <DetailPanel className="max-h-80 overflow-y-auto">
                                <BreakdownBars items={detail.activity.resources} onSelect={(item) => context.openEvents({ actor: login, resource: item.key })} />
                            </DetailPanel>
                        </DetailSection>
                    </div>

                    <DetailSection
                        icon={<ScrollText />}
                        title="Recent events"
                        action={<button type="button" className="saas-section-link" onClick={() => context.openEvents({ actor: login })}>Open in audit log</button>}
                    >
                        <EventList events={detail.recentEvents.slice(0, 25)} onOpen={context.openEvent} />
                    </DetailSection>
                </>
            )}
        </DetailModal>
    );
}
